import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import FcFilterChips from './FcFilterChips';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ProductCosts, CostOutlier } from 'models/ShipBobPerformanceModel';

function money(value: number | null): string {
  return value == null ? '—' : `$${value.toFixed(2)}`;
}

function shortName(composition: string, max = 44): string {
  return composition.length > max
    ? `${composition.slice(0, max)}…`
    : composition;
}

function VsMedian({ pct }: { pct: number | null }) {
  if (pct == null) {
    return <span style={{ color: '#94A3B8' }}>—</span>;
  }
  const up = pct > 0;
  const negligible = Math.abs(pct) < 0.05;
  const color = negligible ? '#64748B' : up ? '#B91C1C' : '#15803D';
  return (
    <span style={{ color, fontWeight: 600 }}>
      {up ? '+' : ''}
      {pct.toFixed(1)}%
    </span>
  );
}

function CopyOrderNumber({ orderNumber }: { orderNumber: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!orderNumber) return <span style={{ color: '#94A3B8' }}>—</span>;
  const copy = () => {
    navigator.clipboard.writeText(orderNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{orderNumber}</span>
      <Tooltip title={copied ? 'Copied!' : 'Copy order #'}>
        <IconButton size="small" onClick={copy} sx={{ p: 0.25 }}>
          {copied ? (
            <CheckIcon sx={{ fontSize: 14, color: '#15803D' }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function csvEscape(value: string | number | null): string {
  const s = value == null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadOutliersCsv(
  outliers: CostOutlier[],
  range: { startDate: string; endDate: string },
  fc: string,
) {
  const header = [
    'ship_date',
    'order_number',
    'shipbob_order_id',
    'shipment_id',
    'basket',
    'fulfillment_center',
    'cost_usd',
    'expected_cost_usd',
    'deviation_usd',
    'z_score',
  ];
  const lines = [
    header.join(','),
    ...outliers.map(o =>
      [
        o.shipDate,
        o.orderNumber,
        o.orderId,
        o.shipmentId,
        o.composition,
        o.fulfillmentCenter,
        o.cost,
        o.expectedCost,
        o.deviation,
        o.z,
      ]
        .map(csvEscape)
        .join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const fcSuffix = fc === 'All' ? '' : `-${fc.replace(/[^a-z0-9]+/gi, '_')}`;
  anchor.href = url;
  anchor.download = `shipbob-cost-outliers-${range.startDate}_${range.endDate}${fcSuffix}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function ProductCostSection({
  productCosts,
}: {
  productCosts: ProductCosts;
}) {
  const { coverage, params } = productCosts;
  const [selectedFc, setSelectedFc] = useState<string>('All');

  // FC chips sorted by shipment volume (top-basket volume as proxy), matching
  // the zones section; FCs that only appear via outliers still get a chip.
  const fcNames = useMemo(() => {
    const volume = new Map<string, number>();
    productCosts.fcCompositions.forEach(row => {
      volume.set(row.fc, (volume.get(row.fc) || 0) + row.shipments);
    });
    productCosts.outliers.forEach(o => {
      if (!volume.has(o.fulfillmentCenter)) volume.set(o.fulfillmentCenter, 0);
    });
    return [...volume.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([fc]) => fc);
  }, [productCosts]);

  // Drift: is the basket's NORMAL cost rising? Median vs median (7d vs 90d),
  // so outliers can't fake an increase — those live in the outlier table.
  const driftPct = (m7: number | null, m90: number | null) =>
    m7 != null && m90 != null && m90 > 0 ? ((m7 - m90) / m90) * 100 : null;

  const basketRows = useMemo(() => {
    if (selectedFc === 'All') {
      return productCosts.compositions.slice(0, 20).map(c => ({
        key: c.composition,
        composition: c.composition,
        shipments: c.shipments,
        sharePct: c.sharePct,
        avgCost: c.avgCost,
        median7Cost: c.median7Cost,
        median30Cost: c.median30Cost,
        median90Cost: c.medianCost,
        driftPct: driftPct(c.median7Cost, c.medianCost),
      }));
    }
    const globalByComposition = new Map(
      productCosts.compositions.map(c => [c.composition, c]),
    );
    const fcRows = productCosts.fcCompositions.filter(
      r => r.fc === selectedFc,
    );
    const fcTotal = fcRows.reduce((s, r) => s + r.shipments, 0) || 1;
    return fcRows.map(r => {
      const global = globalByComposition.get(r.composition);
      return {
        key: `${r.fc}|${r.composition}`,
        composition: r.composition,
        shipments: r.shipments,
        sharePct: (r.shipments / fcTotal) * 100,
        avgCost: r.avgCost,
        median7Cost: global?.median7Cost ?? null,
        median30Cost: global?.median30Cost ?? null,
        median90Cost: r.medianCost,
        driftPct: driftPct(global?.median7Cost ?? null, r.medianCost),
      };
    });
  }, [productCosts, selectedFc]);

  const outlierRows = useMemo(
    () =>
      selectedFc === 'All'
        ? productCosts.outliers
        : productCosts.outliers.filter(
            o => o.fulfillmentCenter === selectedFc,
          ),
    [productCosts, selectedFc],
  );

  // Total paid above expected across the flagged shipments — the dollars at
  // stake in this range (and FC selection).
  const totalOvercharge = useMemo(
    () => outlierRows.reduce((sum, o) => sum + o.deviation, 0),
    [outlierRows],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Product cost breakdown
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          Costs are compared within identical baskets (product mix × quantity).
          Outliers flagged when a shipment costs ≥{params.outlierK}× robust σ{' '}
          <b>more</b> than its basket&apos;s trailing-90-day median (min $2) —
          overcharges only, sorted by cost.
          {coverage.flaggableSharePct != null &&
            ` Coverage: ${coverage.flaggableSharePct.toFixed(0)}% of shipments.`}
        </Typography>
      </Box>

      <FcFilterChips
        fcNames={fcNames}
        selected={selectedFc}
        onSelect={setSelectedFc}
      >
        {selectedFc !== 'All' && (
          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
            baskets &amp; outliers filtered to {selectedFc}; medians &amp;
            drift are the basket&apos;s network-wide norms — compare Avg
            (range) against them
          </Typography>
        )}
      </FcFilterChips>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Top baskets by volume{' '}
          {selectedFc !== 'All' ? `— ${selectedFc}` : ''}
        </Typography>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Basket</TableCell>
                <TableCell align="right">Shipments</TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={
                      selectedFc === 'All'
                        ? 'Share of all shipments in this range'
                        : `Share within ${selectedFc}'s top baskets shown here (not all of its shipments)`
                    }
                  >
                    <span>Share</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Avg (range)</TableCell>
                <TableCell align="right">7d median</TableCell>
                <TableCell align="right">30d median</TableCell>
                <TableCell align="right">90d median</TableCell>
                <TableCell align="right">
                  <Tooltip title="7d median vs 90d median — has this basket's NORMAL cost risen? Medians ignore outliers, so red here means a genuine rate increase (talk to ShipBob about rates).">
                    <span>Drift</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {basketRows.map(row => (
                <TableRow key={row.key} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Tooltip title={row.composition}>
                      <span>{shortName(row.composition, 90)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {row.shipments.toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    {row.sharePct == null ? '—' : `${row.sharePct.toFixed(1)}%`}
                  </TableCell>
                  <TableCell align="right">{money(row.avgCost)}</TableCell>
                  <TableCell align="right">{money(row.median7Cost)}</TableCell>
                  <TableCell align="right">{money(row.median30Cost)}</TableCell>
                  <TableCell align="right">{money(row.median90Cost)}</TableCell>
                  <TableCell align="right">
                    <VsMedian pct={row.driftPct} />
                  </TableCell>
                </TableRow>
              ))}
              {basketRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ color: '#94A3B8' }}>
                    No data for this selection.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2">
            Cost outliers {selectedFc !== 'All' ? `— ${selectedFc}` : ''}
          </Typography>
          <Chip
            size="small"
            label={`${outlierRows.length} flagged`}
            sx={{
              backgroundColor: outlierRows.length > 0 ? '#FEF2F2' : '#F0FDF4',
              color: outlierRows.length > 0 ? '#B91C1C' : '#15803D',
              fontWeight: 700,
            }}
          />
          {totalOvercharge > 0 && (
            <Tooltip
              title={`Total paid above expected across the flagged shipments, ${productCosts.range.startDate} → ${productCosts.range.endDate}${
                selectedFc === 'All' ? '' : ` · ${selectedFc}`
              }`}
            >
              <Chip
                size="small"
                label={`$${totalOvercharge.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} at stake`}
                sx={{
                  backgroundColor: '#FEF2F2',
                  color: '#B91C1C',
                  fontWeight: 700,
                }}
              />
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            disabled={outlierRows.length === 0}
            onClick={() =>
              downloadOutliersCsv(outlierRows, productCosts.range, selectedFc)
            }
          >
            Export CSV
          </Button>
        </Box>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Order #</TableCell>
                <TableCell>Basket</TableCell>
                <TableCell>FC</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Expected</TableCell>
                <TableCell align="right">Δ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outlierRows.map(o => (
                <TableRow key={o.shipmentId} hover>
                  <TableCell>{o.shipDate}</TableCell>
                  <TableCell>
                    <CopyOrderNumber orderNumber={o.orderNumber} />
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={`${o.composition} · shipment ${o.shipmentId} · ShipBob order id ${o.orderId}`}
                    >
                      <span>{shortName(o.composition, 70)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{shortName(o.fulfillmentCenter, 20)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: '#B91C1C', fontWeight: 700 }}
                  >
                    {money(o.cost)}
                  </TableCell>
                  <TableCell align="right">{money(o.expectedCost)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: '#B91C1C', fontWeight: 600 }}
                  >
                    +{money(o.deviation)}
                  </TableCell>
                </TableRow>
              ))}
              {outlierRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ color: '#94A3B8' }}>
                    No cost outliers in this selection. 🎉
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
